import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreate, useUpdate, useNotify, useGetList, useGetOne } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Alert,
  Autocomplete,
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  InputAdornment,
} from "@mui/material";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import {
  AlertType,
  ResourceName,
  type Claimant,
  type DeductibleHistoryItem,
  type EndorsementListItem,
  type Policy,
  type QualifyingCondition,
  type Vendor,
} from "@/types";
import type { DeductibleModificationConfig } from "@/types/endorsementConfig";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

/** Stable empty‐array defaults — prevents useEffect infinite loops when
 *  useGetList returns undefined while loading (a new `[]` ref every render
 *  would re‐trigger effects whose dep arrays include the result). */
const EMPTY_ENDORSEMENTS: EndorsementListItem[] = [];
const EMPTY_VENDORS: Vendor[] = [];
const EMPTY_CLAIMANTS: Claimant[] = [];

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface DeductibleModificationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Claimant ID. When omitted in create mode, a claimant picker is shown. */
  claimantId?: string;
  policyId: string;
  mode: "create" | "edit";
  existingRecord?: DeductibleHistoryItem;
  /** When provided, the endorsement is pre-selected and the selector is hidden. */
  endorsementId?: string;
  onSuccess?: () => void;
  /** When true, the claimant is lasered. Used to block creation when endorsement excludes lasered claimants. */
  isClaimantLasered?: boolean;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export const DeductibleModificationDialog = ({
  open,
  onClose,
  claimantId,
  policyId,
  mode,
  existingRecord,
  endorsementId,
  onSuccess,
  isClaimantLasered = false,
}: DeductibleModificationDialogProps) => {
  const [create] = useCreate();
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  // ── Fetch the policy to pre-fill original deductible ──
  const { data: policy } = useGetOne<Policy>(ResourceName.POLICIES, { id: policyId }, { enabled: open && !!policyId });

  // ── Endorsement dropdown (hidden when endorsementId is provided) ──
  const { data: endorsements = EMPTY_ENDORSEMENTS } = useGetList<EndorsementListItem>(
    `policies/${policyId}/endorsements`,
    {
      pagination: { page: 1, perPage: 100 },
      filter: {},
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: open && !!policyId }
  );

  const deductibleModEndorsements = useMemo(
    () => endorsements.filter((e) => e.type === "deductible_modification"),
    [endorsements]
  );

  // ── Vendor autocomplete (carriers only) ──
  const { data: vendors = EMPTY_VENDORS } = useGetList<Vendor>(
    "vendors/carriers",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "company_name", order: "ASC" },
    },
    { enabled: open }
  );

  // ── Claimant picker (shown when claimantId is not provided) ──
  const needsClaimantPicker = !claimantId && mode === "create";
  const clientId = policy?.client_id_ref;
  const { data: claimants = EMPTY_CLAIMANTS } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: clientId ? { client_id_ref: clientId } : {},
      pagination: { page: 1, perPage: 500 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: open && needsClaimantPicker && !!clientId }
  );
  const [selectedClaimant, setSelectedClaimant] = useState<Claimant | null>(null);
  const resolvedClaimantId = claimantId || selectedClaimant?.id || "";

  // ── Controlled state for prefilled fields ──
  const [selectedEndorsementId, setSelectedEndorsementId] = useState("");
  const [effectiveStart, setEffectiveStart] = useState("");
  const [effectiveEnd, setEffectiveEnd] = useState("");
  const [originalDeductible, setOriginalDeductible] = useState("");
  const [modifiedDeductible, setModifiedDeductible] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  // Resolve which endorsement is selected/active
  const resolvedEndorsementId = endorsementId || selectedEndorsementId;
  const selectedEndorsement = useMemo(
    () => deductibleModEndorsements.find((e) => e.id === resolvedEndorsementId),
    [deductibleModEndorsements, resolvedEndorsementId]
  );

  // ── Check if endorsement excludes lasered claimants ──
  const endorsementExcludesLasered = useMemo(() => {
    if (!selectedEndorsement?.config) return false;
    return (selectedEndorsement.config as DeductibleModificationConfig).is_excludes_lasered === true;
  }, [selectedEndorsement]);

  const isBlockedByLaseredExclusion = isClaimantLasered && endorsementExcludesLasered;

  // ── Derive qualifying conditions from the selected endorsement ──
  const availableQualifyingConditions = useMemo(() => {
    if (!selectedEndorsement) return [] as QualifyingCondition[];
    // Raw useGetList data keeps qualifying_conditions inside config; the
    // useEndorsements hook hoists it to top-level. Handle both shapes.
    const conditions =
      selectedEndorsement.qualifying_conditions ??
      ((selectedEndorsement.config as Record<string, unknown> | null)?.qualifying_conditions as
        | QualifyingCondition[]
        | undefined);
    return conditions ?? ([] as QualifyingCondition[]);
  }, [selectedEndorsement]);

  // ── Initialize form state when dialog opens ──
  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && existingRecord) {
      // Edit mode — populate from existing record
      setSelectedEndorsementId(existingRecord.endorsement_id_ref ?? "");
      setEffectiveStart(existingRecord.effective_period_start ?? "");
      setEffectiveEnd(existingRecord.effective_period_end ?? "");
      setOriginalDeductible(String(existingRecord.original_deductible ?? ""));
      setModifiedDeductible(String(existingRecord.modified_deductible ?? ""));
      const vendorMatch = vendors.find((v) => v.id === existingRecord.approved_vendor_id_ref);
      setSelectedVendor(vendorMatch ?? null);
    } else {
      // Create mode — reset everything
      setSelectedEndorsementId(endorsementId ?? "");
      setEffectiveStart("");
      setEffectiveEnd("");
      setOriginalDeductible("");
      setModifiedDeductible("");
      setSelectedVendor(null);
      setSelectedClaimant(null);
    }
  }, [open, mode, existingRecord, endorsementId, vendors]);

  // ── Auto-select endorsement when only one exists (create mode) ──
  useEffect(() => {
    if (mode !== "create" || endorsementId) return;
    if (deductibleModEndorsements.length === 1 && !selectedEndorsementId) {
      setSelectedEndorsementId(deductibleModEndorsements[0].id);
    }
  }, [mode, endorsementId, deductibleModEndorsements, selectedEndorsementId]);

  // ── Pre-fill effective dates from endorsement valid period (create mode) ──
  useEffect(() => {
    if (mode !== "create" || !selectedEndorsement) return;
    // Only prefill if the user hasn't already entered dates
    if (!effectiveStart && selectedEndorsement.valid_period_start) {
      setEffectiveStart(selectedEndorsement.valid_period_start);
    }
    if (!effectiveEnd && selectedEndorsement.valid_period_end) {
      setEffectiveEnd(selectedEndorsement.valid_period_end);
    }
  }, [mode, selectedEndorsement, effectiveStart, effectiveEnd]);

  // ── Determine whether policy provides the original deductible ──
  const policySpecDeductible = policy?.specific_benefit?.specific_deductible;
  const hasPolicyDeductible =
    policySpecDeductible !== null && policySpecDeductible !== undefined && policySpecDeductible > 0;

  // ── Pre-fill original deductible from policy (create mode) ──
  useEffect(() => {
    if (mode !== "create" || !policy || originalDeductible) return;
    if (hasPolicyDeductible) {
      setOriginalDeductible(String(policySpecDeductible));
    }
  }, [mode, policy, originalDeductible]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);

      const payload = {
        policy_id_ref: policyId,
        endorsement_id_ref: resolvedEndorsementId,
        effective_period_start: effectiveStart,
        effective_period_end: effectiveEnd,
        is_triggered: formData.get("is_triggered") === "on",
        qualifying_condition: formData.get("qualifying_condition") as string,
        trigger_date: formData.get("trigger_date") as string,
        original_deductible: parseFloat(originalDeductible) || 0,
        modified_deductible: parseFloat(modifiedDeductible) || 0,
        approved_vendor_id_ref: selectedVendor?.id ?? "",
        notification_date: formData.get("notification_date") as string,
        notification_reference: formData.get("notification_reference") as string,
      };

      const resource = `claimants/${resolvedClaimantId}/deductible-modifications`;

      try {
        if (mode === "create") {
          await create(resource, { data: payload }, { returnPromise: true });
          notify("Deductible modification created", { type: AlertType.SUCCESS });
        } else if (existingRecord) {
          await update(resource, { id: existingRecord.id, data: payload }, { returnPromise: true });
          notify("Deductible modification updated", { type: AlertType.SUCCESS });
        }
        onClose();
        onSuccess?.();
      } catch (err: unknown) {
        notifyError(err);
      }
    },
    [
      resolvedClaimantId,
      policyId,
      mode,
      existingRecord,
      resolvedEndorsementId,
      effectiveStart,
      effectiveEnd,
      originalDeductible,
      modifiedDeductible,
      selectedVendor,
      create,
      update,
      notify,
      notifyError,
      onClose,
      onSuccess,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle variant="h3" sx={{ borderBottom: 1, borderColor: "divider" }}>
        {mode === "create" ? "Apply Deductible Modification" : "Edit Deductible Modification"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pb: 4 }}>
          <Stack spacing={3}>
            {/* Lasered exclusion warning */}
            {isBlockedByLaseredExclusion && (
              <Alert severity="warning">
                This endorsement excludes lasered claimants. You cannot apply this deductible modification to this
                claimant.
              </Alert>
            )}

            {/* Claimant picker — shown when no claimantId is provided */}
            {needsClaimantPicker && (
              <Autocomplete
                options={claimants}
                getOptionLabel={(c) =>
                  `${c.claimant_last_name}, ${c.claimant_first_name}${c.member_id ? ` (${c.member_id})` : ""}`
                }
                value={selectedClaimant}
                onChange={(_e, newValue) => setSelectedClaimant(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Claimant" required size="small" placeholder="Search claimants…" />
                )}
              />
            )}

            {/* Endorsement selector — hidden when endorsementId is provided */}
            {!endorsementId && (
              <TextField
                label="Endorsement"
                select
                required
                fullWidth
                value={selectedEndorsementId}
                onChange={(e) => {
                  setSelectedEndorsementId(e.target.value);
                  // Reset effective dates so they can be re-prefilled from the new endorsement
                  setEffectiveStart("");
                  setEffectiveEnd("");
                }}
                disabled={mode === "edit"}
                size="small"
              >
                {deductibleModEndorsements.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.label || "Deductible Modification"} ({e.valid_period_start} — {e.valid_period_end})
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Qualifying condition — sourced from the selected endorsement */}
            {resolvedEndorsementId && availableQualifyingConditions.length > 0 ? (
              <TextField
                name="qualifying_condition"
                label="Qualifying Condition"
                select
                required
                fullWidth
                defaultValue={mode === "edit" && existingRecord ? (existingRecord.qualifying_condition ?? "") : ""}
                size="small"
                helperText="Conditions defined on the selected endorsement"
              >
                {availableQualifyingConditions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {formatToTitleCase(c)}
                  </MenuItem>
                ))}
              </TextField>
            ) : resolvedEndorsementId ? (
              <Alert severity="info" variant="outlined" sx={{ py: 0.75 }}>
                No qualifying conditions defined on this endorsement. Add conditions on the endorsement first.
              </Alert>
            ) : (
              <TextField
                name="qualifying_condition"
                label="Qualifying Condition"
                select
                required
                fullWidth
                disabled
                value=""
                size="small"
                helperText="Select an endorsement first"
              >
                <MenuItem value="" />
              </TextField>
            )}

            {/* Trigger date + is_triggered */}
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                name="trigger_date"
                label="Trigger Date"
                type="date"
                fullWidth
                defaultValue={mode === "edit" && existingRecord ? (existingRecord.trigger_date ?? "") : ""}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_triggered"
                    defaultChecked={mode === "edit" && existingRecord ? existingRecord.is_triggered : true}
                  />
                }
                label="Active"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Stack>

            {/* Effective period (controlled — locked from endorsement valid period when endorsementId is provided) */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Effective Start"
                type="date"
                required
                fullWidth
                value={effectiveStart}
                onChange={(e) => setEffectiveStart(e.target.value)}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: { readOnly: mode === "create" && !!endorsementId && !!effectiveStart },
                }}
                helperText={
                  mode === "create" && !!endorsementId && !!effectiveStart ? "From endorsement valid period" : undefined
                }
              />
              <TextField
                label="Effective End"
                type="date"
                required
                fullWidth
                value={effectiveEnd}
                onChange={(e) => setEffectiveEnd(e.target.value)}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: { readOnly: mode === "create" && !!endorsementId && !!effectiveEnd },
                }}
                helperText={
                  mode === "create" && !!endorsementId && !!effectiveEnd ? "From endorsement valid period" : undefined
                }
              />
            </Stack>

            {/* Deductible amounts (controlled — original locked from policy when available) */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Original Deductible"
                type="number"
                required
                fullWidth
                value={originalDeductible}
                onChange={(e) => setOriginalDeductible(e.target.value)}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: mode === "create" && hasPolicyDeductible,
                  },
                }}
                helperText={mode === "create" && hasPolicyDeductible ? "From policy specific deductible" : undefined}
              />
              <TextField
                label="Modified Deductible"
                type="number"
                required
                fullWidth
                value={modifiedDeductible}
                onChange={(e) => setModifiedDeductible(e.target.value)}
                size="small"
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
              />
            </Stack>

            {/* Vendor autocomplete */}
            <Autocomplete
              options={vendors}
              getOptionLabel={(v) => v.company_name || ""}
              value={selectedVendor}
              onChange={(_e, newValue) => setSelectedVendor(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Approved Vendor" size="small" />}
            />

            {/* Notification */}
            <Stack direction="row" spacing={2}>
              <TextField
                name="notification_date"
                label="Notification Date"
                type="date"
                fullWidth
                defaultValue={mode === "edit" && existingRecord ? (existingRecord.notification_date ?? "") : ""}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                name="notification_reference"
                label="Notification Reference"
                fullWidth
                defaultValue={mode === "edit" && existingRecord ? (existingRecord.notification_reference ?? "") : ""}
                size="small"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: "divider", px: 3, py: 2 }}>
          <Box>
            <Button variant={ButtonStyleType.Outlined} size={UIComponentSize.Medium} onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
              variant={ButtonStyleType.Contained}
              size={UIComponentSize.Medium}
              type="submit"
              disabled={isBlockedByLaseredExclusion || !resolvedClaimantId}
            >
              {mode === "create" ? "Apply Modification" : "Save Changes"}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
